import React, { useState, useEffect } from 'react';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';
import toast from 'react-hot-toast';
import { ArrowRightLeft } from 'lucide-react';
import AutoProtoCodeReport from './AutoProtoCodeReport';
import { format, parseISO, isValid } from 'date-fns';

const COLORS = ['#10B981','#F59E0B','#3B82F6','#EF4444','#8B5CF6'];
const safeDateTime = (d) => { try { return d && isValid(parseISO(d)) ? format(parseISO(d), 'dd MMM yyyy, hh:mm a') : '-'; } catch { return '-'; } };

export default function ReportsPage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess, isAdmin } = usePermission('reports');
  const navigate = useNavigate();
  const { formatDate, formatCurrency } = useSettings();
  const [workerReport, setWorkerReport] = useState([]);
  const [deptReport, setDeptReport] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [mrReport, setMrReport] = useState([]);
  const [moRefReport, setMoRefReport] = useState([]);
  const [activeTab, setActiveTab] = useState('workers');
  const [loading, setLoading] = useState(true);
  const [expandedWorker, setExpandedWorker] = useState(null);
  const [workerDetails, setWorkerDetails] = useState({});
  const [selectedTask, setSelectedTask] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/reports/workers'),
      api.get('/reports/departments'),
      api.get('/reports/transfers').catch(() => ({ data: [] })),
      api.get('/reports/material-requests').catch(() => ({ data: [] })),
      api.get('/reports/mo-references').catch(() => ({ data: [] }))
    ]).then(([wRes, dRes, tRes, mrRes, moRefRes]) => {
      setWorkerReport(wRes.data);
      setDeptReport(dRes.data);
      setTransfers(tRes.data);
      setMrReport(mrRes.data);
      setMoRefReport(moRefRes.data);
    }).finally(() => setLoading(false));
  }, []);

  const loadWorkerDetails = async (workerId) => {
    try {
      const res = await api.get(`/tasks/all`);
      console.log('All tasks from API:', res.data);
      // Filter tasks for this specific worker
      const workerTasks = res.data.filter(t => t.worker_id === workerId);
      console.log(`Tasks for worker ${workerId}:`, workerTasks);
      setWorkerDetails(d => ({ ...d, [workerId]: workerTasks }));
    } catch (err) {
      console.error('Error loading worker details:', err);
    }
  };

  const handleExpandWorker = (workerId) => {
    console.log('Expanding worker:', workerId);
    if (expandedWorker === workerId) {
      setExpandedWorker(null);
    } else {
      setExpandedWorker(workerId);
      if (!workerDetails[workerId] || workerDetails[workerId].length === 0) {
        console.log('Loading details for worker:', workerId);
        loadWorkerDetails(workerId);
      }
    }
  };

  const handleTaskClick = (task) => {
    console.log('Task clicked:', task);
    console.log('Task data:', JSON.stringify(task, null, 2));
    console.log('Project ID:', task?.project_id);
    console.log('Task ID:', task?.id);
    
    if (!task || !task.project_id) {
      console.error('Invalid task or missing project_id', task);
      toast.error('Cannot navigate: Task or Project ID is missing');
      return;
    }
    
    console.log(`Navigating to /admin/projects/${task.project_id} with taskId ${task.id}`);
    toast.success('Opening task details...');
    navigate(`/admin/projects/${task.project_id}`, { state: { expandTaskId: task.id } });
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" /></div>;

  const deptPieData = deptReport.map(d => ({ name: d.name, value: d.total_tasks, color: d.color }));

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
        <p className="text-gray-500 text-sm">Performance overview</p>
      </div>

      {/* Tabs — horizontal scroll on mobile */}
      <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        <div className="flex gap-1 border-b border-gray-200 min-w-max sm:min-w-0">
          {[['workers','Worker'],['departments','Departments'],['transfers','Transfers'],['mr','MR Requests'],['mo_refs','MO Images'],['proto','Auto Proto Codes']].map(([key, label]) => (
            <button key={key} onClick={() => setActiveTab(key)} className={`px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors flex items-center gap-1 sm:gap-1.5 whitespace-nowrap ${activeTab === key ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500'}`}>
              {key === 'transfers' && <ArrowRightLeft className="w-3 h-3 sm:w-3.5 sm:h-3.5" />}{label}
              {key === 'transfers' && transfers.length > 0 && <span className="bg-purple-100 text-purple-700 text-xs px-1 sm:px-1.5 py-0.5 rounded-full">{transfers.length}</span>}
              {key === 'mr' && mrReport.length > 0 && <span className="bg-green-100 text-green-700 text-xs px-1 sm:px-1.5 py-0.5 rounded-full">{mrReport.length}</span>}
              {key === 'mo_refs' && moRefReport.length > 0 && <span className="bg-blue-100 text-blue-700 text-xs px-1 sm:px-1.5 py-0.5 rounded-full">{moRefReport.length}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Worker Table */}
      {activeTab === 'workers' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-3 sm:px-5 py-2 sm:py-3 w-8"></th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-3 sm:px-5 py-2 sm:py-3">Worker</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-2 sm:px-4 py-2 sm:py-3">Total</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-2 sm:px-4 py-2 sm:py-3">✅</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-2 sm:px-4 py-2 sm:py-3 hidden sm:table-cell">⏳</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-2 sm:px-4 py-2 sm:py-3 hidden sm:table-cell">⏹</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-2 sm:px-4 py-2 sm:py-3">⚠️</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-2 sm:px-4 py-2 sm:py-3 hidden md:table-cell">Qty</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-2 sm:px-4 py-2 sm:py-3">Eff%</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {workerReport.map(w => {
                    const eff = w.total_qty > 0 ? Math.round((w.done_qty / w.total_qty) * 100) : 0;
                    return (
                      <React.Fragment key={w.id}>
                        <tr className={`hover:bg-gray-50 cursor-pointer transition-colors ${expandedWorker === w.id ? 'bg-blue-50' : ''}`} onClick={() => handleExpandWorker(w.id)}>
                          <td className="px-5 py-3 text-center">
                            <button onClick={(e) => { e.stopPropagation(); handleExpandWorker(w.id); }} className="text-blue-600 hover:text-blue-700 font-bold">
                              {expandedWorker === w.id ? '▼' : '▶'}
                            </button>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center text-amber-700 font-bold text-sm">{w.name.charAt(0)}</div>
                              <span className="font-medium text-gray-900 text-sm">{w.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 font-medium">{w.total_tasks || 0}</td>
                          <td className="px-4 py-3"><span className="text-sm text-green-600 font-bold">{w.completed || 0}</span></td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 hidden sm:table-cell"><span className="text-sm text-blue-600 font-bold">{w.in_progress || 0}</span></td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 hidden sm:table-cell"><span className="text-sm text-yellow-600 font-bold">{w.pending || 0}</span></td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3"><span className={`text-sm font-bold ${w.delayed > 0 ? 'text-red-600' : 'text-gray-400'}`}>{w.delayed || 0}</span></td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 hidden md:table-cell text-sm text-gray-600 font-medium">{w.done_qty || 0}/{w.total_qty || 0}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-16 bg-gray-100 rounded-full h-2">
                                <div className="bg-green-500 h-2 rounded-full" style={{ width: `${eff}%` }} />
                              </div>
                              <span className="text-xs text-gray-600 font-semibold">{eff}%</span>
                            </div>
                          </td>
                        </tr>
                        {/* Expanded Details Row */}
                        {expandedWorker === w.id && (
                          <tr className="bg-blue-50">
                            <td colSpan="9" className="px-5 py-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Completed Tasks */}
                                <div className="bg-white rounded-xl border border-green-200 p-4">
                                  <h4 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                                    <span className="w-5 h-5 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">✓</span>
                                    Completed ({w.completed || 0})
                                  </h4>
                                  <div className="space-y-2 max-h-40 overflow-y-auto">
                                    {workerDetails[w.id]?.filter(t => t.status === 'completed').map(t => (
                                      <div key={t.id} onClick={() => handleTaskClick(t)} className="text-xs bg-green-50 p-2.5 rounded border border-green-100 cursor-pointer hover:bg-green-100 hover:border-green-300 transition-all space-y-1">
                                        <p className="font-semibold text-gray-900">{t.task_title}</p>
                                        <p className="text-gray-600"><span className="font-medium">Project:</span> {t.project_name} <span className="font-mono text-gray-500">(ID: {t.project_id})</span></p>
                                        {t.customer_name && <p className="text-gray-600"><span className="font-medium">Customer:</span> {t.customer_name}</p>}
                                        {t.item_name && <p className="text-gray-600"><span className="font-medium">Item:</span> {t.item_name}</p>}
                                        {t.photo_id && <p className="text-gray-500"><span className="font-medium">📷 Photo ID:</span> {t.photo_id}</p>}
                                        <p className="text-gray-500">✅ {t.quantity_completed}/{t.quantity_assigned}</p>
                                      </div>
                                    )) || <p className="text-xs text-gray-400">Loading...</p>}
                                  </div>
                                </div>

                                {/* In Progress Tasks */}
                                <div className="bg-white rounded-xl border border-blue-200 p-4">
                                  <h4 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                                    <span className="w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center">⏳</span>
                                    In Progress ({w.in_progress || 0})
                                  </h4>
                                  <div className="space-y-2 max-h-40 overflow-y-auto">
                                    {workerDetails[w.id]?.filter(t => t.status === 'in_progress').map(t => (
                                      <div key={t.id} onClick={() => handleTaskClick(t)} className="text-xs bg-blue-50 p-2.5 rounded border border-blue-100 cursor-pointer hover:bg-blue-100 hover:border-blue-300 transition-all space-y-1">
                                        <p className="font-semibold text-gray-900">{t.task_title}</p>
                                        <p className="text-gray-600"><span className="font-medium">Project:</span> {t.project_name} <span className="font-mono text-gray-500">(ID: {t.project_id})</span></p>
                                        {t.customer_name && <p className="text-gray-600"><span className="font-medium">Customer:</span> {t.customer_name}</p>}
                                        {t.item_name && <p className="text-gray-600"><span className="font-medium">Item:</span> {t.item_name}</p>}
                                        {t.photo_id && <p className="text-gray-500"><span className="font-medium">📷 Photo ID:</span> {t.photo_id}</p>}
                                        <div className="mt-1 bg-gray-200 rounded h-1.5">
                                          <div className="bg-blue-500 h-1.5 rounded" style={{ width: `${(t.quantity_completed / t.quantity_assigned) * 100}%` }} />
                                        </div>
                                        <p className="text-gray-600">{t.quantity_completed}/{t.quantity_assigned} ({Math.round((t.quantity_completed / t.quantity_assigned) * 100)}%)</p>
                                      </div>
                                    )) || <p className="text-xs text-gray-400">Loading...</p>}
                                  </div>
                                </div>

                                {/* Pending/Other Tasks */}
                                <div className="bg-white rounded-xl border border-yellow-200 p-4">
                                  <h4 className="font-semibold text-yellow-700 mb-3 flex items-center gap-2">
                                    <span className="w-5 h-5 rounded-full bg-yellow-500 text-white text-xs flex items-center justify-center">⏹</span>
                                    Pending ({w.pending || 0})
                                  </h4>
                                  <div className="space-y-2 max-h-40 overflow-y-auto">
                                    {workerDetails[w.id]?.filter(t => ['pending', 'waiting', 'on_hold'].includes(t.status)).map(t => (
                                      <div key={t.id} onClick={() => handleTaskClick(t)} className="text-xs bg-yellow-50 p-2.5 rounded border border-yellow-100 cursor-pointer hover:bg-yellow-100 hover:border-yellow-300 transition-all space-y-1">
                                        <p className="font-semibold text-gray-900">{t.task_title}</p>
                                        <p className="text-gray-600"><span className="font-medium">Project:</span> {t.project_name} <span className="font-mono text-gray-500">(ID: {t.project_id})</span></p>
                                        {t.customer_name && <p className="text-gray-600"><span className="font-medium">Customer:</span> {t.customer_name}</p>}
                                        {t.item_name && <p className="text-gray-600"><span className="font-medium">Item:</span> {t.item_name}</p>}
                                        {t.photo_id && <p className="text-gray-500"><span className="font-medium">📷 Photo ID:</span> {t.photo_id}</p>}
                                        <p className="text-yellow-700 font-medium capitalize">{t.status}</p>
                                      </div>
                                    )) || <p className="text-xs text-gray-400">No pending tasks</p>}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Dept Table */}
      {activeTab === 'departments' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3">Department</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Workers</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Total Tasks</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Completed</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">In Progress</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Delayed</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Qty Efficiency</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {deptReport.map(d => {
                  const eff = d.total_qty > 0 ? Math.round((d.done_qty / d.total_qty) * 100) : 0;
                  return (
                    <tr key={d.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                          <span className="font-medium text-gray-900 text-sm">{d.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{d.workers || 0}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-700">{d.total_tasks || 0}</td>
                      <td className="px-4 py-3"><span className="text-sm text-green-600 font-medium">{d.completed || 0}</span></td>
                      <td className="px-4 py-3"><span className="text-sm text-blue-600 font-medium">{d.in_progress || 0}</span></td>
                      <td className="px-4 py-3"><span className={`text-sm font-medium ${d.delayed > 0 ? 'text-red-600' : 'text-gray-400'}`}>{d.delayed || 0}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-gray-100 rounded-full h-2">
                            <div className="h-2 rounded-full" style={{ width: `${eff}%`, backgroundColor: d.color }} />
                          </div>
                          <span className="text-xs text-gray-600 font-medium">{eff}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {activeTab === 'mr' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Material Request Images (Project-wise)</h3>
            <span className="text-sm text-gray-500">{mrReport.length} projects</span>
          </div>
          {mrReport.length === 0 ? (
            <div className="p-12 text-center text-gray-400">No MR data found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Project</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Project Code</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">MR Requests</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">MR Images</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Latest Image</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {mrReport.map(r => (
                    <tr key={r.project_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{r.project_name}</td>
                      <td className="px-4 py-3 text-gray-600">{r.proj_code}</td>
                      <td className="px-4 py-3 text-gray-700">{r.mr_requests}</td>
                      <td className="px-4 py-3 text-gray-700">{r.mr_images}</td>
                      <td className="px-4 py-3 text-gray-500">{r.latest_image ? new Date(r.latest_image).toLocaleString('en-IN') : 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'mo_refs' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">MO Reference Images (Project-wise)</h3>
            <span className="text-sm text-gray-500">{moRefReport.length} projects</span>
          </div>
          {moRefReport.length === 0 ? (
            <div className="p-12 text-center text-gray-400">No MO reference image data found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Project</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Project Code</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">MO Images</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Latest Upload</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Download</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {moRefReport.map(r => (
                    <tr key={r.project_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{r.project_name}</td>
                      <td className="px-4 py-3 text-gray-600">{r.proj_code}</td>
                      <td className="px-4 py-3 text-gray-700">{r.image_count}</td>
                      <td className="px-4 py-3 text-gray-500">{r.latest_image_created_at ? new Date(r.latest_image_created_at).toLocaleString('en-IN') : 'N/A'}</td>
                      <td className="px-4 py-3 text-gray-500">
                        {r.latest_image_url ? (
                          <a className="text-blue-600 hover:text-blue-800 underline text-xs" href={r.latest_image_url} target="_blank" rel="noreferrer" download>
                            Download
                          </a>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Transfer History Table */}
      {activeTab === 'transfers' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4 text-purple-600" /> Transfer History
            </h3>
            <span className="text-sm text-gray-500">{transfers.length} transfers total</span>
          </div>
          {transfers.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <ArrowRightLeft className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No transfers yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {['Task','Project','From Worker','To Worker','Transferred By','Reason','Date'].map(h => (
                      <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {transfers.map(t => (
                    <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{t.task_title}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full status-${t.task_status}`}>{t.task_status}</span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-700">{t.project_name}</p>
                        <p className="text-xs text-gray-400 font-mono">{t.proj_code}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-red-600 font-medium">{t.from_worker_name || '—'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-green-600 font-medium">{t.to_worker_name || '—'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${t.transferred_by_role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                          {t.transferred_by_name} ({t.transferred_by_role})
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-32">
                        {t.reason || <span className="text-gray-300 italic">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                        {safeDateTime(t.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'proto' && <AutoProtoCodeReport />}

      {/* Task Details Modal */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{selectedTask.task_title}</h2>
                <p className="text-sm text-gray-500 mt-1">{selectedTask.project_name}</p>
              </div>
              <button onClick={() => setSelectedTask(null)} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status Badges */}
              <div className="flex flex-wrap gap-2">
                <span className={`text-sm px-3 py-1.5 rounded-full font-medium status-${selectedTask.status}`}>{selectedTask.status}</span>
                {selectedTask.priority && <span className={`text-sm px-3 py-1.5 rounded-full font-medium priority-${selectedTask.priority} capitalize`}>{selectedTask.priority}</span>}
                {selectedTask.assign_type && <span className={`text-sm px-3 py-1.5 rounded-full font-medium ${selectedTask.assign_type === 'worker' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>Assigned to {selectedTask.assign_type}</span>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Project */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Project</p>
                  <p className="text-sm font-medium text-gray-900">{selectedTask.project_name}</p>
                  {selectedTask.proj_code && <p className="text-xs text-gray-400 font-mono mt-1">{selectedTask.proj_code}</p>}
                </div>

                {/* Item */}
                {selectedTask.item_name && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Item</p>
                    <p className="text-sm font-medium text-gray-900">{selectedTask.item_name}</p>
                    {selectedTask.proto_code && <p className="text-xs text-gray-400 font-mono mt-1">{selectedTask.proto_code}</p>}
                  </div>
                )}

                {/* Assigned To */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Assigned To</p>
                  <p className="text-sm font-medium text-gray-900">{selectedTask.worker_name || selectedTask.department_name || 'Unassigned'}</p>
                  {selectedTask.department_name && <p className="text-xs text-gray-400 mt-1">Department</p>}
                </div>

                {/* Quantity */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Quantity Progress</p>
                  <p className="text-lg font-bold text-gray-900">{selectedTask.quantity_completed}/{selectedTask.quantity_assigned}</p>
                  {selectedTask.quantity_assigned > 0 && (
                    <p className="text-xs text-gray-500 mt-1">{Math.round((selectedTask.quantity_completed / selectedTask.quantity_assigned) * 100)}% complete</p>
                  )}
                </div>
              </div>

              {/* Progress Bar */}
              {selectedTask.quantity_assigned > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Progress</p>
                  <div className="w-full bg-gray-100 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all ${
                        selectedTask.status === 'completed' ? 'bg-green-500' :
                        selectedTask.status === 'in_progress' ? 'bg-blue-500' :
                        'bg-gray-400'
                      }`}
                      style={{ width: `${(selectedTask.quantity_completed / selectedTask.quantity_assigned) * 100}%` }} 
                    />
                  </div>
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                {selectedTask.start_date && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Start Date</p>
                    <p className="text-sm text-gray-900">{formatDate(selectedTask.start_date)}</p>
                  </div>
                )}
                {selectedTask.due_date && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Due Date</p>
                    <p className="text-sm text-gray-900">{formatDate(selectedTask.due_date)}</p>
                  </div>
                )}
              </div>

              {/* Description */}
              {selectedTask.task_description && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Description</p>
                  <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{selectedTask.task_description}</p>
                </div>
              )}

              {/* Admin Notes */}
              {selectedTask.admin_notes && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Admin Notes</p>
                  <p className="text-sm text-gray-700 bg-blue-50 rounded-lg p-3 border border-blue-200">{selectedTask.admin_notes}</p>
                </div>
              )}

              {/* Close Button */}
              <button 
                onClick={() => setSelectedTask(null)}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-xl font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  
    </PermissionGuard>
  );
}
